
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { InfoIcon, TrendingUp, TrendingDown, ChevronDown, ChevronUp } from 'lucide-react';
import { getScoreColor } from '@/utils/scoreCalculation';
import { DoctorScore } from '@/types/scoring';

interface PerformanceScoreCardProps {
  score: DoctorScore;
  isDoctor?: boolean;
}

const PerformanceScoreCard: React.FC<PerformanceScoreCardProps> = ({ 
  score, 
  isDoctor = true 
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const colorClass = getScoreColor(score.totalScore);

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-medium">
          Performance Score
          <Button 
            variant="link" 
            className="px-1 py-0 h-auto text-muted-foreground"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </Button>
        </CardTitle>
        
        <div className="flex items-center gap-2">
          <div className={`text-2xl font-bold ${colorClass}`}>
            {score.totalScore}
          </div>
          {score.trend && (
            <div className={`flex items-center text-sm ${
              score.trend === 'up' ? 'text-green-500' : 
              score.trend === 'down' ? 'text-red-500' : 
              'text-muted-foreground'
            }`}>
              {score.trend === 'up' ? 
                <TrendingUp size={16} /> : 
                score.trend === 'down' ? 
                <TrendingDown size={16} /> : 
                null
              }
              {score.trendPercentage && 
                <span className="ml-1">
                  {score.trend === 'up' ? '+' : ''}{score.trendPercentage.toFixed(1)}%
                </span>
              }
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent>
        <div>
          <div className="mb-2 flex items-center gap-1">
            <div className={`h-2.5 w-2.5 rounded-full ${
              score.totalScore >= 85 ? 'bg-green-500' :
              score.totalScore >= 70 ? 'bg-emerald-500' :
              score.totalScore >= 60 ? 'bg-yellow-500' :
              'bg-red-500'
            }`}></div>
            <span className="text-sm">
              {score.totalScore >= 85 ? 'Top Performer' :
               score.totalScore >= 70 ? 'Good Standing' :
               score.totalScore >= 60 ? 'Needs Improvement' :
               'Low Performance'}
            </span>
          </div>
          
          {expanded && (
            <div className="mt-4 space-y-4">
              {score.breakdown.map((category, index) => (
                <div key={index} className="mb-2">
                  <div className="flex justify-between text-sm mb-1">
                    <div className="flex items-center gap-1">
                      <span>{category.category}</span>
                      <Button variant="ghost" size="icon" className="h-4 w-4 p-0">
                        <InfoIcon size={12} className="text-muted-foreground" />
                      </Button>
                    </div>
                    <span className={`font-medium ${getScoreColor(category.score)}`}>
                      {category.score}/100
                    </span>
                  </div>
                  <Progress 
                    value={category.score} 
                    max={100}
                    className={`h-1.5 ${
                      category.score >= 85 ? 'bg-green-100' :
                      category.score >= 70 ? 'bg-emerald-100' :
                      category.score >= 60 ? 'bg-yellow-100' :
                      'bg-red-100'
                    }`}
                  />
                </div>
              ))}
              
              <div className="text-xs text-muted-foreground mt-2">
                Last updated: {new Date(score.lastUpdated).toLocaleDateString()}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PerformanceScoreCard;
